import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import type { Contract, PaginatedResponse } from '@/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { FileText, DollarSign, TrendingUp } from 'lucide-react';


const statusVariant = (status: string) => {
    switch (status) {
        case 'active': return 'success' as const;
        case 'closed': return 'secondary' as const;
        case 'abandoned': return 'destructive' as const;
        default: return 'default' as const;
    }
};

export default function DashboardPage() {
    const { user } = useAuth();
    const { data, isLoading } = useQuery({
        queryKey: ['contracts'],
        queryFn: () => api.get<PaginatedResponse<Contract>>('/contracts'),
    });

    const contracts = data?.data ?? [];
    const activeContracts = contracts.filter((c) => c.status === 'active');
    const totalBalance = activeContracts.reduce(
        (sum, c) => sum + parseFloat(c.current_balance),
        0
    );
    const totalPaid = activeContracts.reduce(
        (sum, c) => sum + parseFloat(c.total_paid),
        0
    );

    return (
        <div className="space-y-6">
            <h1 className="text-xl font-semibold">
                Welcome, {user?.display_name ?? 'User'}
            </h1>

            {/* Stats */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Card>
                    <CardContent className="flex items-center gap-3 p-4">
                        <FileText className="h-8 w-8 text-violet-400" />
                        <div>
                            <p className="text-2xl font-bold">{activeContracts.length}</p>
                            <p className="text-xs text-slate-400">Active Contracts</p>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="flex items-center gap-3 p-4">
                        <DollarSign className="h-8 w-8 text-red-500" />
                        <div>
                            <p className="text-2xl font-bold">{formatCurrency(totalBalance)}</p>
                            <p className="text-xs text-slate-400">Total Balance</p>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="flex items-center gap-3 p-4">
                        <TrendingUp className="h-8 w-8 text-green-500" />
                        <div>
                            <p className="text-2xl font-bold">{formatCurrency(totalPaid)}</p>
                            <p className="text-xs text-slate-400">Total Paid</p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Contract list */}
            <div>
                <h2 className="mb-3 text-lg font-semibold">Your Contracts</h2>
                {isLoading ? (
                    <p className="text-sm text-slate-400">Loading...</p>
                ) : contracts.length === 0 ? (
                    <Card>
                        <CardContent className="py-8 text-center text-sm text-slate-400">
                            No contracts yet.{' '}
                            <Link to="/marketplace" className="text-violet-400 hover:underline">
                                Browse the marketplace
                            </Link>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="space-y-3">
                        {contracts.map((contract) => (
                            <Link key={contract.id} to={`/contracts/${contract.id}`} className="block">
                                <Card className="transition-shadow hover:shadow-lg hover:shadow-slate-900/50">
                                    <CardContent className="flex items-center justify-between p-4">
                                        <div className="min-w-0">
                                            <p className="truncate font-medium">{contract.title}</p>
                                            <p className="text-xs text-slate-400">
                                                {user?.role === 'dom' || user?.role === 'admin' || (user?.role === 'switch' && contract.dom?.id === user.id)
                                                    ? <>Sub: <span className="text-violet-400">{contract.sub!.display_name}</span></>
                                                    : <>Dom: <span className="text-violet-400">{contract.dom!.display_name}</span></>}
                                            </p>
                                        </div>
                                        <div className="flex flex-col items-end gap-1">
                                            <Badge variant={statusVariant(contract.status)}>
                                                {contract.status}
                                            </Badge>
                                            <span className="text-sm font-semibold">
                                                {formatCurrency(contract.current_balance)}
                                            </span>
                                        </div>
                                    </CardContent>
                                </Card>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
